"use client";

import { ActiveOffersProps, Offer } from "@/src/types/offers";
import React, { useState } from "react";
import { toast } from "sonner";

const THEMES = ["sun", "green", "indigo", "pink"] as const;
const ICONS = ["percent", "gift", "card", "bundle"] as const;

const THEME_STYLES: Record<string, { bg: string; border: string; iconColor: string }> = {
  sun:    { bg: "linear-gradient(135deg, #FFE9DF 0%, #FFF4DA 100%)", border: "rgba(242, 98, 80,0.3)", iconColor: "var(--rising-sun)" },
  green:  { bg: "linear-gradient(135deg, #E8F0E5 0%, #F2F7F0 100%)", border: "rgba(53,73,48,0.25)", iconColor: "var(--chutney-green)" },
  indigo: { bg: "linear-gradient(135deg, #E0EAF7 0%, #EEF3FB 100%)", border: "rgba(8,61,118,0.25)", iconColor: "var(--indigo)" },
  pink:   { bg: "linear-gradient(135deg, #FDE6E0 0%, #FFF1ED 100%)", border: "rgba(244,172,153,0.5)", iconColor: "var(--rising-sun)" },
};

function OfferIcon({ type, color }: { type: string; color: string }) {
  const style = { color };
  if (type === "percent") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={style}>
      <line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
  if (type === "gift") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" />
      <line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z" />
      <path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z" />
    </svg>
  );
  if (type === "card") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  );
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

export const ActiveOffers: React.FC<ActiveOffersProps & { subtext?: string }> = ({
  coupons = [],
  tenantName = "this shop",
  heading,
  subtext,
}) => {
  const [copied, setCopied] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const offers: Offer[] = coupons.filter((c) => c.code).map((c, i) => ({
    id: String(c.id),
    icon: ICONS[i % ICONS.length],
    title: c.name,
    desc: c.description,
    code: c.code ?? "",
    expiry: undefined,
    theme: THEMES[i % THEMES.length],
  }));

  if (offers.length === 0) return null;

  const handleCopy = async (code: string) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(code);
        toast.success('Code copied to clipboard');
      } else {
        const ta = document.createElement("textarea");
        ta.value = code;
        ta.style.cssText = "position:fixed;opacity:0;pointer-events:none";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success('Code copied to clipboard');
      }
    } catch {
      toast.error('Failed to copy code. Please try copying manually.');

    }
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <section className="mt-[clamp(40px,5vw,64px)]">
      {/* Section head */}
      <div className="mb-[clamp(16px,2vw,22px)] flex items-end justify-between gap-4">
        <div>
          <h2
            className="leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "clamp(20px,2.6vw,30px)", letterSpacing: "-0.01em" }}
          >
            {heading || `Active offers from ${tenantName}`}
          </h2>
          <p className="mt-1 text-sm" style={{ color: "var(--techno-black-50)" }}>
            {subtext || "Tap to copy. Conditions apply."}
          </p>
        </div>
        {offers.length > 6 && (
          <button
            onClick={() => setShowAll((p) => !p)}
            className="flex shrink-0 items-center gap-1.5 text-sm font-semibold transition-opacity hover:opacity-70"
            style={{ color: "var(--navy)" }}
          >
            {showAll ? "Show less" : `See all ${offers.length}`}
          </button>
        )}
      </div>

      {/* Slim ticket row */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(showAll ? offers : offers.slice(0, 6)).map((offer) => {
          const isCopied = copied === offer.code;
          return (
            <div
              key={offer.id}
              className="relative flex items-center gap-3.5 overflow-hidden rounded-[16px] border p-4 pl-5 transition-transform hover:-translate-y-0.5"
              style={{ background: "var(--surface)", borderColor: "var(--border-soft)", boxShadow: "var(--shadow-soft)" }}
            >
              {/* Coral edge */}
              <span className="absolute left-0 top-0 h-full" style={{ width: 4, background: "var(--coral)" }} />
              {/* Icon */}
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                style={{ background: "var(--coral-soft)" }}
              >
                <OfferIcon type={offer.icon} color="var(--coral)" />
              </span>
              {/* Title + desc */}
              <div className="min-w-0 flex-1">
                <div className="truncate font-bold leading-snug" style={{ fontSize: 14.5, color: "var(--techno-black)" }}>
                  {offer.title}
                </div>
                {offer.desc && (
                  <div className="truncate text-[12px]" style={{ color: "var(--techno-black-50)" }}>
                    {offer.desc}
                  </div>
                )}
              </div>
              {/* Code + copy */}
              <code
                className="hidden shrink-0 rounded-md border px-2.5 py-1.5 font-bold tracking-[0.5px] md:block lg:hidden xl:block"
                style={{ fontFamily: "var(--font-code)", fontSize: "11.5px", color: "var(--navy)", background: "var(--navy-tint)", borderColor: "var(--border-soft)", borderStyle: "dashed" }}
              >
                {offer.code}
              </code>
              <button
                onClick={() => handleCopy(offer.code)}
                className="flex min-h-[40px] shrink-0 items-center rounded-lg px-3.5 py-2 text-[12.5px] font-bold transition cursor-pointer"
                style={{ background: isCopied ? "var(--coral-soft)" : "var(--navy)", color: isCopied ? "var(--coral-deep)" : "#fff" }}
              >
                {isCopied ? "Copied" : "Copy"}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};
